// Which sheets a connect raises when it lands on the conversation page.
//
// signup-meetings § What each shows (Mark, 2026-10-16; kept at checkpoint 1, 2026-10-17):
//   first meeting → "Where you met", then the share sheet
//   re-meet       → "Where you met", then the share sheet (a new meeting needs a place)
//   reconnect     → the share sheet only (same meeting, place already set)
// Every exchange path stages here before navigating; ProfileConnectionPage reads it once.
//
// Stored per room under the session key the exchange has always used
// (`navigatedRoomsToContactSelection`). QRScreen asks `isRoomStaged` whether the
// conversation page owns a room's channel. Older entries are a bare `true` (a first
// connect: both sheets).

import { MEETING_KIND } from '../meetings/meetingRule';

const STAGE_KEY = 'navigatedRoomsToContactSelection';
/** The once-per-landing flag the conversation page sets when the share sheet shows. */
export const shareSheetShownKey = (roomId) => `share_sheet_shown_${roomId}`;

const readAll = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

/**
 * @param {string} roomId - the room the person lands in
 * @param {'first'|'reconnect'|'remeet'} kind - the meeting verdict ("Where you met"
 *   unless it's the same meeting)
 * @param {Object} [p]
 * @param {boolean} [p.repeat] - they already know each other: the share sheet shows even
 *   though they shared before
 */
export const stageConnectSheets = (roomId, kind, { repeat = false } = {}) => {
  if (!roomId) return;
  const all = readAll();
  all[roomId] = { whereYouMet: kind !== MEETING_KIND.RECONNECT, repeat: !!repeat };
  sessionStorage.setItem(STAGE_KEY, JSON.stringify(all));
  // A repeat connect in the same session must raise the sheet again
  sessionStorage.removeItem(shareSheetShownKey(roomId));
};

/** Whether a connect staged this room — the conversation page then owns its channel. */
export const isRoomStaged = (roomId) => readStagedSheets(roomId) !== null;

/** The sheets staged for a room, or null when this landing isn't a connect. */
export const readStagedSheets = (roomId) => {
  const staged = roomId ? readAll()[roomId] : null;
  if (!staged) return null;
  if (staged === true) return { whereYouMet: true, repeat: false };
  return { whereYouMet: !!staged.whereYouMet, repeat: !!staged.repeat };
};
